'use client';

import { useCallback, useState } from 'react';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import { rawField, readJson, stringField } from '@/lib/jsonBody';

const readErrorRule = (body: unknown): string | undefined =>
  stringField(rawField(rawField(body, 'error'), 'details'), 'rule');

interface AuthPostActionOptions {
  path: string;
  tag: string;
  errorText: string;
  errorTextByRule?: Record<string, string>;
  onSuccess?: (data: unknown) => void;
}

export function useAuthPostAction({
  path,
  tag,
  errorText,
  errorTextByRule,
  onSuccess,
}: AuthPostActionOptions) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (body?: Record<string, unknown>) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${getApiBaseUrl()}${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'shot',
          },
          credentials: 'include',
          body: JSON.stringify(body ?? {}),
        });

        if (!response.ok) {
          const errorData: unknown = await readJson(response).catch(() => null);
          console.error(`[${tag}] API error:`, response.status, errorData);
          const rule = readErrorRule(errorData);
          setError((rule && errorTextByRule?.[rule]) || errorText);
          return;
        }

        // WHY: ручка привязки Telegram отвечает пустым телом, остальные — `{ userId }`: пустое тело не отказ
        onSuccess?.(await readJson(response).catch(() => null));
      } catch (err) {
        console.error(`[${tag}] Error:`, err);
        setError(errorText);
      } finally {
        setLoading(false);
      }
    },
    [path, tag, errorText, errorTextByRule, onSuccess],
  );

  return { loading, error, setError, run };
}
